import * as amplitude from '@amplitude/analytics-browser';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Invoice, { Tip as InvoiceTip } from '@spike/invoice-model';
import { InvoicesStatus, saveTipThunk } from '@spike/invoices-action';
import { Option } from '@spike/model';
import { PaymentMethodIds } from '@spike/payments-model';
import useNonInitialEffect from '@versiondos/hooks';
import PaymentMethods from './PaymentMethods';
import SendingInformation from './SendingInformation';
import Tips from './Tips';
import { isSameTip } from './Tips/TipUtils';
import { AMPLITUDE } from 'constants/index';
import { useApiClientWrapper } from 'hooks';
import { useHasPremiumPlan } from 'hooks/premiumPlan';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { isTemporalEmail } from 'utils/ValidationUtils';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { Typography } from '@material-ui/core';
import Section from './Section';

export interface CheckoutPaymentProps {
    invoice: Invoice;
    paymentsEnabled: boolean;
    paymentMethods?: Array<PaymentMethodIds>;
    hideTips?: boolean;
    disabled?: boolean;
    initPaymentMethodId?: string | null;
    className?: string;
    onSavingTip: (saving: boolean) => void;
    onChangePaymentInfo: (paymentInfo: PaymentInfo) => void;
}

export interface PaymentInfo {
    paymentMethodId: string | null;
    tip: InvoiceTip | null;
    email: string | null;
    sendInvoice: boolean;
    posTerminalId: number | null;
    posServiceId: string | null;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        section: {
            marginTop: 37,
            [theme.breakpoints.down('sm')]: {
                marginTop: 24
            }
        },
        alert: {
            marginBottom: '0px !important',
            height: 53
        },
        alertText: {
            fontSize: 14,
            lineHeight: '20px'
        }
    })
);

export const CheckoutPayment: FunctionComponent<CheckoutPaymentProps> = props => {
    const classes = useStyles(props);

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const hasPremiumPlan = useHasPremiumPlan();

    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);

    const [paymentInfo, setPaymentInfo] = useState<PaymentInfo>({
        paymentMethodId: props.initPaymentMethodId ?? null,
        tip: props.invoice.tip,
        email: isTemporalEmail(props.invoice?.customer?.email ?? '') ? '' : props.invoice?.customer?.email ?? null,
        sendInvoice:
            !isTemporalEmail(props.invoice?.customer?.email ?? '') &&
            (props.invoice?.customer?.email ?? '').trim().length > 0,
        posTerminalId: null,
        posServiceId: null
    });

    const [show, setShow] = useState({
        tips: props.initPaymentMethodId !== PaymentMethodIds.POS,
        tipsAlert: props.initPaymentMethodId === PaymentMethodIds.POS
    });

    const [prevTip, setPrevTip] = useState<InvoiceTip | null>(props.invoice.tip ? { ...props.invoice.tip } : null);

    const [saving, setSaving] = useState(false);

    useNonInitialEffect(() => {
        switch (invoicesStatus) {
            case InvoicesStatus.SaveTipSuccess:
            case InvoicesStatus.Error:
                props.onSavingTip(false);
                setSaving(false);
                break;
        }
    }, [invoicesStatus]);

    useEffect(() => props.onChangePaymentInfo(paymentInfo), [paymentInfo]);

    useNonInitialEffect(() => {
        if (Number(props.invoice.balance) <= 0) {
            changePaymentMethodHandler(null);
        }
    }, [props.invoice.balance]);

    const saveTip = (amount: string | null, percentage: string | null) => {
        props.onSavingTip(true);
        setSaving(true);
        dispatch(saveTipThunk(apiClientWrapper, props.invoice.id!, amount, percentage));
    };

    const changePaymentMethodHandler = (paymentMethod: Option<string> | null) => {
        if (paymentMethod === null) {
            setPaymentInfo(prev => ({
                ...prev,
                paymentMethodId: null
            }));
            setShow({
                tips: true,
                tipsAlert: false
            });
        } else {
            if (paymentMethod.id === PaymentMethodIds.POS) {
                Number(props.invoice.tip?.amount) !== Number(props.invoice.balance) && saveTip(null, null);
            } else if (paymentInfo.paymentMethodId === PaymentMethodIds.POS) {
                saveTip(prevTip?.amount || null, prevTip?.percentage || null);
            }

            setPaymentInfo(prev => ({
                ...prev,
                paymentMethodId: paymentMethod.id,
                tip:
                    paymentMethod.id === PaymentMethodIds.POS
                        ? null
                        : prev.paymentMethodId === PaymentMethodIds.POS
                        ? prevTip
                        : prev.tip
            }));

            setShow({
                tips: paymentMethod.id !== PaymentMethodIds.POS,
                tipsAlert: paymentMethod.id === PaymentMethodIds.POS
            });

            switch (paymentMethod.id) {
                case PaymentMethodIds.Cash:
                    amplitude.track(AMPLITUDE.CTA_CASH_CHECK_OUT);
                    break;
                case PaymentMethodIds.POS:
                    amplitude.track(AMPLITUDE.CTA_POS_CHECK_OUT);
                    break;
                case PaymentMethodIds.CreditCard:
                    amplitude.track(AMPLITUDE.CTA_CREDIT_CARD_CHECK_OUT);
                    break;
                case PaymentMethodIds.Other:
                    amplitude.track(AMPLITUDE.CTA_OTHER_CHECK_OUT);
                    break;
                default:
                    break;
            }
        }
    };

    const changeTipHandler = (tip: InvoiceTip | null) => {
        if (!isSameTip(paymentInfo.tip, tip)) {
            setPaymentInfo(prev => ({ ...prev, tip }));
            setPrevTip(tip);
            saveTip(tip === null ? null : tip.amount, tip === null ? null : tip.percentage);
        }
    };

    const changeSendInvoiceHandler = (sendInvoice: boolean, email: string, isEmailValid: boolean) => {
        setPaymentInfo(prev => ({ ...prev, sendInvoice, email: isEmailValid ? email : null }));
    };

    return (
        <>
            <PaymentMethods
                hideTitle={true}
                paymentsEnabled={props.paymentsEnabled}
                paymentMethods={props.paymentMethods}
                selectedId={paymentInfo.paymentMethodId}
                hasPremiumPlan={hasPremiumPlan}
                disabled={props.disabled || Number(props.invoice.balance) <= 0}
                onSelect={changePaymentMethodHandler}
                source="booking"
            />
            {show.tips && !props.hideTips && (
                <Tips
                    invoiceId={props.invoice.id!}
                    invoiceTip={paymentInfo.tip}
                    subtotalWithoutTipTaxes={props.invoice.subtotal}
                    tipDistribution={props.invoice.tipDistribution ?? []}
                    saving={saving}
                    disabled={props.disabled}
                    className={classes.section}
                    onChange={changeTipHandler}
                />
            )}
            {show.tipsAlert && (
                <Section
                    title="Add a tip"
                    className={classes.section}
                >
                    <AlertNewBooking
                        className={classes.alert}
                        bWidth="2px"
                        icon={faExclamationCircle}
                        iconSize="lg"
                        iconColor="#BAA997"
                        bgColor="#F8F5F1"
                        bdColor="#BCB8AE"
                    >
                        <Typography className={classes.alertText}>
                            You can select the tip directly in the terminal.
                        </Typography>
                    </AlertNewBooking>
                </Section>
            )}
            <SendingInformation
                className={classes.section}
                email={props.invoice.customer?.email ?? ''}
                disabled={props.disabled}
                onChange={changeSendInvoiceHandler}
            />
        </>
    );
};

export default CheckoutPayment;
